import { assembleLpSmbHomeContent, isLpSmbHomeContentUsable, isLpSmbHomeRunReady } from "./lp-express-smb-home";

export type HomeLifecycle = { status: "rendering" | "ready" | "failed" | "stuck"; message: string | null };

export function isSmbHomeAttempt(gc: any): boolean {
  return gc?.studioDesign?.rendererTemplateKey
    ? gc.studioDesign.rendererTemplateKey === "smb-home"
    : gc?.templateType === "smb-home";
}

/** The server's read-only classifier is authoritative. The fallback also makes
 * cached/older responses expire locally without discarding a known remote job. */
export function resolveSmbHomeLifecycle(run: {
  status: unknown; generatedContent?: any; studioStatus?: HomeLifecycle["status"]; errorMessage?: unknown;
}, now = Date.now()): HomeLifecycle {
  const gc = run.generatedContent ?? {};
  const design = gc.studioBrandDesign;
  const imageSlots = gc.smbHomeImages?.slots;
  const imageIncomplete = Array.isArray(imageSlots) && imageSlots.some((slot: any) => slot.status !== "ready" || !slot.url);
  const imageAmbiguous = imageIncomplete && imageSlots.some((slot: any) => slot.status === "ambiguous");
  const imageActive = imageIncomplete && Date.parse(gc.smbHomeImageWorker?.leaseExpiresAt) > now;
  const imageFailed = imageIncomplete && imageSlots.some((slot: any) => ["blocked", "failed"].includes(slot.status));
  const ambiguous = design?.submissionState === "ambiguous" || imageAmbiguous;
  const remotePending = design?.status === "pending" && !ambiguous &&
    Boolean(design.mpEditorId || design.paidRevisionRequestId);
  const usable = isLpSmbHomeContentUsable(assembleLpSmbHomeContent(gc));
  const lease = Date.parse(design?.leaseExpiresAt || gc.studioCreation?.leaseExpiresAt);
  const status = imageAmbiguous ? "stuck" : imageActive ? "rendering" : imageFailed ? "failed" :
    remotePending ? "rendering" : ambiguous ? "stuck" : run.studioStatus ??
    (run.status === "failed" || design?.status === "failed" ? "failed"
      : isLpSmbHomeRunReady(run) ? "ready"
        : run.status === "completed" ? "stuck"
          : Number.isFinite(lease) && lease > now ? "rendering" : "stuck");
  if (status === "rendering" || status === "ready") return { status, message: null };
  if (imageIncomplete) return { status, message: imageAmbiguous
    ? "An image submission is uncertain. Your Home copy is saved; an operator must inspect the image slot before a new paid request."
    : "Your Home copy is saved. Review image progress below to recover accepted renders or retry only failed images." };
  const retained = "Your saved attempt is retained. Check its status before starting another generation; checking does not submit a new request.";
  if (ambiguous) return { status, message: `The Home page design submission is uncertain. An operator must inspect the saved admission before any retry. ${retained}` };
  if (status === "failed") {
    // These fields are the existing user-safe error projection, not provider diagnostics.
    const error = [gc.heroError, design?.failureReason, run.errorMessage]
      .find(value => typeof value === "string" && value.trim());
    return { status, message: `${error || "Home page generation failed."} ${retained}` };
  }
  if (run.status === "completed" && !usable)
    return { status, message: `The saved Home page finished without usable copy. An operator must inspect the retained result; it cannot be opened as a finished page. ${retained}` };
  return { status, message: `The saved Home page is incomplete or its execution was interrupted. No active execution could be confirmed. An operator can inspect the retained admission and any existing design result. ${retained}` };
}
